import { memo, useContext, useEffect, useRef, useState } from 'react';

import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import useUserStore from '@lcsc-ui/store/user';
import Utils from '@lcsc-ui/utils/Utils';
import { message, Modal } from 'antd';
import { throttle } from 'lodash-es';

import Api from './api';
import CouponsList from './components/CouponsList';
import FilterCondition from './components/FilterCondition';
import LeftNav from './components/LeftNav';
import { QbCode } from './enums';

const getNavAndListData = ({ originPartitionList = [], couponBagList = [], couponModelVOListMap = {} }) => {
  const dataListState = [];
  const leftNavListState = [
    {
      frontPartitionCode: '77',
      frontPartitionName: '我的优惠券',
    },
  ];
  originPartitionList.forEach((item) => {
    const list = item.frontPartitionCode === QbCode ? couponBagList : couponModelVOListMap[item.frontPartitionCode];
    if (Array.isArray(list) && list.length) {
      dataListState.push({
        ...item,
        children: list,
      });
      leftNavListState.push(item);
    }
  });
  return {
    dataListState,
    leftNavListState,
  };
};
const GetCoupons = (props) => {
  const { resourceData } = useContext(LayoutContext);
  const { userInfo } = useUserStore();

  const { couponBrandList, couponCatalogList, couponTypeList, partitionList, couponsDataList } = props;
  const initRes = getNavAndListData({
    originPartitionList: partitionList,
    couponBagList: couponsDataList?.couponBagList,
    couponModelVOListMap: couponsDataList?.couponModelVOListMap,
  });
  const [leftNavList, setLeftNavList] = useState(initRes.leftNavListState);
  const [dataList, setDataList] = useState(initRes.dataListState);
  const [sendSearchParams, setSendSearchParams] = useState({
    couponType: '',
    sortColumn: '',
    sortType: '',
    searchValue: '',
    searchCatalogStr: '',
    searchBrandNameStr: '',
    catalogId: '',
  });
  const [loading, setLoading] = useState(false);
  const leftNavRef = useRef(null);
  const [couponCount, setCouponCount] = useState(couponsDataList?.couponCount);
  const [left, setLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [needResetPage, setNeedResetPage] = useState(false);
  const couponsListRef = useRef(null);

  useEffect(() => {
    updateLeft();
    window.addEventListener('resize', updateLeft);
    return () => {
      window.removeEventListener('resize', updateLeft);
    };
  }, []);

  const updateLeft = () => {
    if (couponsListRef.current) {
      const rect = couponsListRef.current.getBoundingClientRect();
      setLeft(rect.left + window.scrollX);
    }
  };

  useEffect(() => {
    if (userInfo?.customerLogin === 1) {
      getCouponsFun(sendSearchParams, true);
    }
  }, [userInfo?.customerLogin]);

  useEffect(() => {
    if (leftNavList.length) {
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [leftNavList]);

  const handleScroll = throttle(() => {
    setScrollTop(window.scrollY);
    leftNavRef?.current?.setLeftNavPosition();
  }, 200);

  const getCouponsFun = async (params = {}, needResetTop = true) => {
    if (loading) return;
    setLoading(true);
    const res = await Api.getCouponsDataList({
      ...sendSearchParams,
      ...params,
    }).finally(() => {
      setLoading(false);
      setNeedResetPage(needResetTop);
    });
    if (res.code === 200) {
      const { dataListState, leftNavListState } = getNavAndListData({
        originPartitionList: partitionList,
        couponBagList: res?.result?.couponBagList,
        couponModelVOListMap: res?.result?.couponModelVOListMap,
      });
      setCouponCount(res?.result?.couponCount);
      setLeftNavList(leftNavListState);
      if (needResetTop && leftNavListState.length) {
        leftNavRef?.current?.toTop(leftNavListState[0].frontPartitionCode);
      }
      setDataList(dataListState);
    }
  };

  const changeSendSearchParams = (params = {}) => {
    const updateParams = {
      ...sendSearchParams,
      ...params,
    };
    setSendSearchParams(updateParams);
    getCouponsFun(updateParams, true);
  };

  const reloadCouponsStatus = async (item, cb) => {
    const response = await Api.getCouponById(item.uuid).finally(() => {
      typeof cb === 'function' && cb();
    });
    if (response?.result === 'success') {
      const { customerMaxNum, isDayLimit } = response.couponModel;
      const endDate = Utils.dateFormat(new Date(response.useCouponEndDate), 'yyyy年MM月dd日');
      let startDate = '';
      if (response.useCouponStartDate) {
        startDate = Utils.dateFormat(new Date(response.useCouponStartDate), 'yyyy年MM月dd日');
      }
      let msg = `优惠券领取成功，有效日期${startDate}至${endDate}${response.couponModel && response.couponModel.limitProductCodes ? `, 以下商品不可用：${response.couponModel.limitProductCodes}` : ''}`;
      if (item.useChannel === 'app') {
        msg += ',限移动端下单使用';
      }
      if (customerMaxNum && customerMaxNum !== 1) {
        msg += `,单客编限领取：${customerMaxNum}张${isDayLimit ? '/天' : ''}`;
      }
      message.success(msg);
      getCouponsFun(sendSearchParams, false);
    } else if (response && response.result && response.result === 'not_plus') {
      Modal.confirm({
        title: '错误提示',
        content: '很抱歉，此券为PLUS会员专享，您目前还不是PLUS会员！',
        okText: '升级PLUS',
        onOk() {
          window.location.href = `${resourceData.projectConfigVO.lcscActivityUrl}/plusIndex.html?from=lqzx`;
        },
        cancelText: '取消',
        onCancel() {},
      });
    } else if (response?.result === 'needLogin') {
      Utils.showWindowLogin();
    } else {
      message.error(response.msg);
    }
  };

  return (
    <>
      <section className={'bg-white w-[1200px] rounded-[10px] m-auto pl-[20px] pb-[10px] pr-[20px] mt-[20px]'}>
        <FilterCondition
          couponBrandList={couponBrandList}
          couponCatalogList={couponCatalogList}
          couponTypeList={couponTypeList}
          sendSearchParams={sendSearchParams}
          changeSendSearchParams={changeSendSearchParams}
        />
      </section>

      <div className={'bg-[#F6F8FA] w-[1200px] m-auto pb-[60px]'} ref={couponsListRef}>
        <CouponsList
          dataList={dataList}
          reloadCouponsStatus={reloadCouponsStatus}
          couponCount={couponCount}
          needResetPage={needResetPage}
        />
      </div>

      {leftNavList.length > 0 && (
        <LeftNav leftNavList={leftNavList} ref={leftNavRef} left={left} scrollTop={scrollTop} />
      )}
    </>
  );
};
export default memo(GetCoupons);
